import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { probeDetailTabs, probeMasterTabs } from 'common/enums';
import {
    ProbeFilter,
    ProbeGroupHistoryItem,
    ProbeGroupListItem,
    ProbeGroupModalErrors,
    ProbeGroupModalItem,
    ProbeHistoryItem,
    ProbeScenarioGroupItem,
    ProbeState,
    RunProbesModalErrors,
    RunProbesModalItem,
} from './types';

const defaultFilter: ProbeFilter = {
    pageSize: 10,
    pageNumber: 1,
    searchString: '',
};

const initialState: ProbeState = {
    masterSpinner: false,
    detailSpinner: false,
    masterSource: {
        data: [],
        total: 0,
        filter: { ...defaultFilter },
    },
    detailSource: {
        data: [],
        total: 0,
        filter: { ...defaultFilter },
    },
    probeGroupModal: {
        open: false,
        locked: false,
        item: {},
        errors: {},
    },
    runProbesModal: {
        open: false,
        locked: false,
        item: {},
        errors: {},
    },
    runProbesConfirmationDialog: {
        open: false,
    },
    masterSelected: [],
    detailSelected: [],
    masterLevel: probeMasterTabs.files,
    detailLevel: probeDetailTabs.caseGroups,
};

const probeSlice = createSlice({
    name: 'probe',
    initialState,
    reducers: {
        toggleMasterSpinner(state, action: PayloadAction<boolean>) {
            state.masterSpinner = action.payload;
        },
        toggleDetailSpinner(state, action: PayloadAction<boolean>) {
            state.detailSpinner = action.payload;
        },
        changeMasterSelection(state, action: PayloadAction<number[]>) {
            state.masterSelected = action.payload;
        },
        changeDetailSelection(state, action: PayloadAction<number[]>) {
            state.detailSelected = action.payload;
        },
        changeMasterLevel(state, action: PayloadAction<number>) {
            state.masterLevel = action.payload;
        },
        changeDetailLevel(state, action: PayloadAction<number>) {
            state.detailLevel = action.payload;
        },
        setMasterSource(
            state,
            action: PayloadAction<{
                data: (ProbeGroupListItem | ProbeGroupHistoryItem)[];
                total: number;
                filter?: Partial<ProbeFilter>;
            }>
        ) {
            state.masterSource.data = action.payload.data;
            state.masterSource.total = action.payload.total;
            state.masterSource.filter = {
                ...state.masterSource.filter,
                ...(action.payload.filter || {}),
            };
        },
        setDetailSource(
            state,
            action: PayloadAction<{
                data: (ProbeScenarioGroupItem | ProbeHistoryItem)[];
                total: number;
                filter?: Partial<ProbeFilter>;
            }>
        ) {
            state.detailSource.data = action.payload.data;
            state.detailSource.total = action.payload.total;
            state.detailSource.filter = {
                ...state.detailSource.filter,
                ...(action.payload.filter || {}),
            };
        },
        setMasterFilter(state, action: PayloadAction<Partial<ProbeFilter>>) {
            state.masterSource.filter = {
                ...state.masterSource.filter,
                ...action.payload,
            };
        },
        setDetailFilter(state, action: PayloadAction<Partial<ProbeFilter>>) {
            state.detailSource.filter = {
                ...state.detailSource.filter,
                ...action.payload,
            };
        },
        setProbeGroupModalSource(state, action: PayloadAction<ProbeGroupModalItem | null>) {
            state.probeGroupModal.item = action.payload || {};
        },
        toggleProbeGroupModalLocked(state, action: PayloadAction<boolean>) {
            state.probeGroupModal.locked = action.payload;
        },
        toggleProbeGroupModal(state, action: PayloadAction<boolean>) {
            state.probeGroupModal.open = action.payload;
        },
        setProbeGroupModalErrors(state, action: PayloadAction<ProbeGroupModalErrors | null>) {
            state.probeGroupModal.errors = action.payload || {};
        },
        setRunProbesModalSource(state, action: PayloadAction<RunProbesModalItem | null>) {
            state.runProbesModal.item = action.payload || {};
        },
        toggleRunProbesModalLocked(state, action: PayloadAction<boolean>) {
            state.runProbesModal.locked = action.payload;
        },
        toggleRunProbesModal(state, action: PayloadAction<boolean>) {
            state.runProbesModal.open = action.payload;
        },
        setRunProbesModalErrors(state, action: PayloadAction<RunProbesModalErrors | null>) {
            state.runProbesModal.errors = action.payload || {};
        },
        toggleRunProbesConfirmationDialog(state, action: PayloadAction<boolean>) {
            state.runProbesConfirmationDialog.open = action.payload;
        },
        reset() {
            return { ...initialState };
        },
    },
});

export const actions = probeSlice.actions;
export default probeSlice.reducer;
